import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { contactsApi } from "../../api/contacts";
import { Modal } from "../Modal";
import type { Contact } from "../../types";

// ---------------------------------------------------------------------------
// Enroll modal — contact full-text search → caller-provided enroll action.
// Campaign and pipeline boards parametrize it with their own onEnroll.
// ---------------------------------------------------------------------------

interface Props {
  alreadyEnrolled: Set<string>;
  onEnroll: (contact: Contact) => Promise<void>;
  onClose: () => void;
}

export function EnrollModal({ alreadyEnrolled, onEnroll, onClose }: Props) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const results = useQuery({
    queryKey: ["contacts-search", q],
    queryFn: () => contactsApi.list({ q: q || undefined, limit: 20 }),
    enabled: q.length > 0,
  });

  async function enroll(contact: Contact) {
    setBusy(true);
    setMessage(null);
    try {
      await onEnroll(contact);
      setMessage(
        `Enrolled ${contact.data.personal.first_name} ${contact.data.personal.last_name}`,
      );
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Enroll contact"
      title="Search contacts"
      widthClass="max-w-[560px]"
      initialFocusRef={searchRef}
      footer={
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      }
    >
          <input
            ref={searchRef}
            className="input w-full"
            placeholder="Name, slug or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {message ? (
            <p className="text-sm text-ink-medium mt-3">{message}</p>
          ) : null}
          <div className="mt-4 max-h-80 overflow-y-auto">
            {q.length === 0 ? (
              <p className="text-sm text-ink-low">Type to search contacts.</p>
            ) : results.isLoading ? (
              <p className="text-sm text-ink-low">Searching…</p>
            ) : results.data && results.data.length > 0 ? (
              <ul className="divide-y" style={{ borderColor: "var(--c-rule)" }}>
                {results.data.map((c) => {
                  const enrolled = alreadyEnrolled.has(c.id);
                  return (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-ink truncate">
                          {c.data.personal.first_name}{" "}
                          {c.data.personal.last_name}
                        </div>
                        <div className="text-xs text-ink-low truncate">
                          {c.id}
                          {c.data.professional?.company_id
                            ? ` · ${c.data.professional.company_id}`
                            : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-main btn-sm"
                        disabled={busy || enrolled}
                        onClick={() => enroll(c)}
                      >
                        {enrolled ? "Already enrolled" : "Enroll"}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-ink-low">No matches.</p>
            )}
          </div>
    </Modal>
  );
}
